import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import VideoReferenceCards from '@/components/chat/ChatMessage/VideoReferenceCards';
import type { ChatMessage } from '@/models/chat-message';

interface AssistantMessageProps {
  message: ChatMessage;
  debugMode?: boolean;
}

export default function AssistantMessage({ message, debugMode = false }: AssistantMessageProps) {
  return (
    <div className="relative">
      {debugMode && (message.model || message.llmLevel) && (
        <div className="absolute top-2 right-2 flex gap-2">
          {message.model && (
            <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
              {message.model}
            </span>
          )}
          {message.llmLevel && (
            <span className="px-2 py-1 text-xs bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded">
              {message.llmLevel}
            </span>
          )}
        </div>
      )}
      <div className="max-w-none">
        <div className="py-4">
          {debugMode ? (
            <div className="whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100 font-mono text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded">
              {message.content}
            </div>
          ) : (
            <div className="max-w-none text-gray-900 dark:text-gray-100">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Customize headings
                  h1: ({ children, ...props }) => (
                    <h1 className="text-2xl font-bold mb-4 mt-6 text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </h1>
                  ),
                  h2: ({ children, ...props }) => (
                    <h2 className="text-xl font-bold mb-3 mt-5 text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </h2>
                  ),
                  h3: ({ children, ...props }) => (
                    <h3 className="text-lg font-bold mb-2 mt-4 text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </h3>
                  ),
                  // Customize paragraphs
                  p: ({ children, ...props }) => (
                    <p className="mb-4 leading-relaxed text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </p>
                  ),
                  // Customize lists
                  ul: ({ children, ...props }) => (
                    <ul className="mb-4 ml-6 list-disc text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children, ...props }) => (
                    <ol className="mb-4 ml-6 list-decimal text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </ol>
                  ),
                  li: ({ children, ...props }) => (
                    <li className="mb-1 text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </li>
                  ),
                  // Customize code blocks
                  code: ({ children, ...props }) => (
                    <code
                      className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono text-gray-900 dark:text-gray-100"
                      {...props}
                    >
                      {children}
                    </code>
                  ),
                  // Customize pre blocks
                  pre: ({ children, ...props }) => (
                    <pre
                      className="bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-x-auto text-sm mb-4 text-gray-900 dark:text-gray-100"
                      {...props}
                    >
                      {children}
                    </pre>
                  ),
                  // Customize blockquotes
                  blockquote: ({ children, ...props }) => (
                    <blockquote
                      className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 my-4 italic text-gray-700 dark:text-gray-300"
                      {...props}
                    >
                      {children}
                    </blockquote>
                  ),
                  // Customize links
                  a: ({ children, ...props }) => (
                    <a
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                  // Customize strong/bold
                  strong: ({ children, ...props }) => (
                    <strong className="font-bold text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </strong>
                  ),
                  // Customize emphasis/italic
                  em: ({ children, ...props }) => (
                    <em className="italic text-gray-900 dark:text-gray-100" {...props}>
                      {children}
                    </em>
                  ),
                  // Customize tables
                  table: ({ children, ...props }) => (
                    <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" {...props}>
                          {children}
                        </table>
                      </div>
                    </div>
                  ),
                  thead: ({ children, ...props }) => (
                    <thead className="bg-gray-50 dark:bg-gray-900" {...props}>
                      {children}
                    </thead>
                  ),
                  tbody: ({ children, ...props }) => (
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" {...props}>
                      {children}
                    </tbody>
                  ),
                  tr: ({ children, ...props }) => (
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700" {...props}>
                      {children}
                    </tr>
                  ),
                  th: ({ children, ...props }) => (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" {...props}>
                      {children}
                    </th>
                  ),
                  td: ({ children, ...props }) => (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" {...props}>
                      {children}
                    </td>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {/* Video-Referenzen (Video-Finder) - null wenn das Backend keine Lookup-Daten mitliefert */}
          {message.videos !== null && message.videos.length > 0 && (
            <VideoReferenceCards videos={message.videos} />
          )}
        </div>
      </div>
    </div>
  );
}